import { useState, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Reusable hook that syncs filter state with URL search params.
 * Filters persist across page refresh and can be shared via URL.
 *
 * @param {object} defaultFilters - Default filter values (used for reset and initial state)
 * @param {object} options - { numberFields: string[] } fields that should be parsed as numbers
 */
export const useUrlFilters = (defaultFilters = {}, { numberFields = [] } = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filters from URL, falling back to defaults
  const [filters, setFilters] = useState(() => {
    const initial = { ...defaultFilters };
    for (const key of Object.keys(defaultFilters)) {
      const urlValue = searchParams.get(key);
      if (urlValue !== null && urlValue !== "") {
        initial[key] = numberFields.includes(key) ? Number(urlValue) : urlValue;
      }
    }
    return initial;
  });

  // Sync filters to URL whenever they change
  useEffect(() => {
    const params = {};
    for (const [key, value] of Object.entries(filters)) {
      // Only include non-default, non-empty values in URL
      if (value !== "" && value !== defaultFilters[key]) {
        params[key] = String(value);
      }
    }
    // Preserve page param if it exists and isn't being managed by filters
    const page = searchParams.get("page");
    if (page && page !== "1" && !("page" in defaultFilters)) {
      params.page = page;
    }
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Update a single filter (resets dependent fields)
  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => {
      const updated = { ...prev, [key]: value };
      if (key === "district_id") updated.taluka_id = "";
      if (key === "category_id") updated.sub_category_id = "";
      return updated;
    });
    // Reset page to 1 on filter change (except search — handled by debounce)
    if (key !== "search") {
      setSearchParams((prev) => {
        const params = Object.fromEntries(prev.entries());
        params.page = "1";
        return params;
      }, { replace: true });
    }
  }, [setSearchParams]);

  // Reset all filters to defaults
  const resetFilters = useCallback(() => {
    setFilters({ ...defaultFilters });
    setSearchParams({ page: "1" }, { replace: true });
  }, [defaultFilters, setSearchParams]);

  // Get current page from URL
  const currentPage = parseInt(searchParams.get("page") || "1", 10);

  // Set page in URL
  const setPage = useCallback((page) => {
    setSearchParams((prev) => {
      const params = Object.fromEntries(prev.entries());
      params.page = String(page);
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  return {
    filters,
    updateFilter,
    resetFilters,
    currentPage,
    setPage,
  };
};

export default useUrlFilters;
